/**
 * Exercice « sur la feuille » pour une question ISOLÉE (Warmup, diagnostic,
 * automatismes, test de pack, démo) : la feuille sous la question, les
 * propositions longues écrites sur la feuille, et le dock du bas (touches +
 * « Check », palette quand on écrit). Remplace la liste de `ChoiceButton`.
 *
 * La feuille : la VRAIE feuille publiée de la question (moteur v2, données de
 * l'énoncé, réponse trouvée marquée ✓ sur sa touche) quand elle en a une
 * (`useQuestionSheet`, sans HORA) ; sinon la feuille libre « pour calculer ».
 *
 * L'écran garde sa logique : `onChoose(id)` reçoit le choix au « Check »,
 * `selected` / `revealed` reviennent de lui après la réponse.
 */

import { useEffect, useMemo, useState } from 'react';
import type { ExerciseVisualBlock } from '@jeraisonne/ai';
import {
  AnswerDock,
  AnswerKeys,
  PaperChoiceList,
  choiceKeyLayout,
  dockMode,
  toggleChoice,
  type PaperQcm,
} from '@jeraisonne/ui/paper';
import {
  InlineSheet,
  SheetPalette,
  useCrossedChoices,
  useFoundChoices,
  useSessionState,
} from '@/features/scratch-sheet';
import type { ScratchSession } from '@/features/scratch-sheet';
import { numbersInText } from '@/features/scratch-sheet/free-sheet';
import { useFreeSheet, useFreeV2Sheet } from '@/features/scratch-sheet/use-free-sheet';
import { useQuestionSheet } from '@/features/scratch-sheet/use-question-sheet';

export interface PaperChoice {
  id: string;
  label_md: string;
  is_correct: boolean;
  visuals?: ExerciseVisualBlock[];
}

function PaletteInDock({ session, onDone }: { session: ScratchSession; onDone: () => void }) {
  const state = useSessionState(session);
  return <SheetPalette state={state} session={session} onClose={onDone} />;
}

export function PaperChoices({
  questionKey,
  promptMd,
  choices,
  selected,
  revealed,
  busy,
  onChoose,
  storageKey,
  error,
  level = null,
}: {
  /** change à chaque question (remet la sélection et la feuille à zéro) */
  questionKey: string;
  /** énoncé : ses nombres deviennent des bouts de la feuille libre */
  promptMd: string;
  choices: PaperChoice[];
  /** choix envoyé (revient de l'écran) */
  selected: string | null;
  revealed: boolean;
  busy: boolean;
  onChoose: (id: string) => void;
  /** lignes du brouillon gardées sur l'appareil ; null = pas gardées */
  storageKey: string | null;
  error?: string | null;
  /** niveau de la question (« 3e », « tle »…) : la feuille libre du moteur v2 joue à ce niveau */
  level?: string | null;
}) {
  const [pick, setPick] = useState<string[]>([]);
  const [writing, setWriting] = useState(false);
  useEffect(() => {
    setPick([]);
    setWriting(false);
  }, [questionKey]);

  const numbers = useMemo(() => numbersInText(promptMd), [promptMd]);
  const session = useFreeSheet({ questionKey, numbers, storageKey, locked: revealed });
  // la feuille du moteur v2 (son clavier, ses outils), construite à partir de l'énoncé et des propositions ;
  // tant qu'elle n'est pas chargée, la feuille libre classique
  const freeInput = useMemo(
    () => ({ promptMd, choices: choices.map((c) => c.label_md), level }),
    [promptMd, choices, level],
  );
  // la feuille publiée de la question, si elle en a une ; la feuille libre seulement sinon
  const statement = useMemo(() => ({ promptMd, level }), [promptMd, level]);
  const published = useQuestionSheet({ questionKey, storageKey, locked: revealed, statement });
  const free = useFreeV2Sheet({
    questionKey,
    input: freeInput,
    storageKey,
    locked: revealed,
    enabled: published === null,
  });
  const v2 = published ?? (published === null ? free : null);
  const found = useFoundChoices(published?.base ?? null);
  const crossed = useCrossedChoices(published?.base ?? null);

  const options = useMemo(
    () =>
      choices.map((c) => ({
        id: c.id,
        label: c.label_md,
        visuals: c.visuals ?? [],
        is_correct: c.is_correct,
      })),
    [choices],
  );
  const layout = useMemo(
    () =>
      choiceKeyLayout(
        options.map((o) => ({ id: o.id, label: o.label, hasVisuals: o.visuals.length > 0 })),
      ),
    [options],
  );

  const qcm: PaperQcm = {
    options,
    multiple: false,
    layout,
    selected: revealed ? [] : pick,
    chosen: selected ? [selected] : [],
    revealed,
    shown: true,
    busy,
    toggle: (id) => {
      if (revealed || busy) return;
      setPick((p) => toggleChoice(p, id, false));
    },
    check: () => {
      if (revealed || busy || pick.length !== 1) return;
      setWriting(false);
      onChoose(pick[0]!);
    },
  };

  const mode = dockMode({
    hasCheckpoint: true,
    answerMode: 'single',
    answered: revealed,
    clueHuntPending: false,
    canAdvance: false,
    sheetWritable: !revealed,
    writing,
  });

  return (
    <>
      <PaperChoiceList qcm={qcm} />
      <div className="mt-2">
        {v2 ? (
          <v2.ui.V2SheetView session={v2.session} onActivate={() => setWriting(true)} />
        ) : (
          <InlineSheet session={session} writing={writing} onActivate={() => setWriting(true)} />
        )}
      </div>
      <AnswerDock label={mode === 'write' ? 'Feuille de brouillon' : 'Ta réponse'}>
        {mode === 'write' ? (
          v2 ? (
            <v2.ui.V2Keyboard
              session={v2.session}
              onAnswer={() => setWriting(false)}
              answerLabel="Répondre"
            />
          ) : (
            <PaletteInDock session={session} onDone={() => setWriting(false)} />
          )
        ) : (
          <AnswerKeys
            qcm={qcm}
            error={error}
            found={found}
            crossed={crossed}
            onWrite={!revealed ? () => setWriting(true) : undefined}
          />
        )}
      </AnswerDock>
    </>
  );
}
